import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import '../styles/Dashboard.css';

interface Child { name: string; age: string; gender: string; allergies: string; specialInstructions: string; }
interface Booking { id: string; sitterName: string; sitterEmail: string; parentName: string; parentEmail: string; parentPhone: string; emergencyPhone?: string; date: string; startTime: string; endTime: string; numChildren?: number | string; children?: Child[]; address: string; timestamp: string; status: string; declineReason?: string; totalPrice?: number; hourlyRate?: number; parentAvatar?: string; }
// interface Order { id: string; parentEmail: string; parentName: string; signature?: string; itemName: string; selectedEdition?: string; price: number; quantity?: number; totalPrice?: number; address?: string; deliveryDate?: string; deliveryType?: string; status: string; timestamp: string; }
interface MessageReply { id: string; user: string; comment: string; timestamp: string; }
interface ContactMessage { id: string; name: string; email: string; message: string; replies: MessageReply[]; timestamp: string; }
interface Event { id: string; title: string; date: string; time: string; location: string; description: string; }

const Dashboard: React.FC = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  // const [orders, setOrders] = useState<Order[]>([]);
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Sitter-only states
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [eventFormData, setEventFormData] = useState({ title: '', date: '', time: '', location: '', description: '' });
  const [decliningBookingId, setDecliningBookingId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = d.getDate() - day; // Start at Sunday
    return new Date(d.setDate(diff));
  });
  const [editingDate, setEditingDate] = useState<{ date: string, monthKey: string } | null>(null);
  const [dateEditorData, setDateEditorData] = useState({ status: 'Free', note: '' });

  // Profile Editor State
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || '',
    category: 'Junior Sitter',
    price: 15,
    description: '',
    detailedDescription: '',
    neighborhood: '',
    weeklyAvailability: {
      'Monday': 'Free', 'Tuesday': 'Free', 'Wednesday': 'Free', 'Thursday': 'Free', 'Friday': 'Free', 'Saturday': 'Free', 'Sunday': 'Free'
    } as Record<string, string>,
    monthlyAvailability: {} as Record<string, Record<string, { status: string, note: string }>>, 
    hours: 'Mon-Thu: 3:20pm–9pm | Fri: 3:20pm–9:30pm | Sat: 10am–9:30pm | Sun: 10am–9pm',
    minAge: 1,
    screenshots: [] as string[]
  });

  // const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const fetchDashboardData = useCallback(() => {
    fetch(`${API_URL}/api/bookings`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json()).then((data: Booking[]) => setBookings(Array.isArray(data) ? data.reverse() : []));

    // fetch(`${API_URL}/api/orders`, { headers: { 'Authorization': `Bearer ${token}` } })
    //  .then(res => res.json()).then((data: Order[]) => setOrders(Array.isArray(data) ? data.reverse() : []));

    fetch(`${API_URL}/api/events`)
      .then(res => res.json()).then((data: Event[]) => setEvents(Array.isArray(data) ? data.reverse() : []));

    if (user?.id.startsWith('sitter-')) {
      fetch(`${API_URL}/api/contact`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.json()).then((data: ContactMessage[]) => setContactMessages(Array.isArray(data) ? data.reverse() : []));

      fetch(`${API_URL}/api/apps/me`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.json()).then(data => { 
          if (!data.error) {
            setProfile(prev => ({
              ...prev,
              ...data,
              screenshots: data.screenshots || []
            }));
          } 
        });
    }
    setLoading(false);
  }, [token, user?.id]);

  useEffect(() => {
    if (!authLoading && user && token) {
      fetchDashboardData();
      const interval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchDashboardData();
        }
      }, 30000); // Check every 30 seconds when tab is active
      return () => clearInterval(interval);
    }
  }, [authLoading, user, token, fetchDashboardData]);

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check total size of the profile (including images)
    const jsonString = JSON.stringify(profile);
    const sizeInMb = jsonString.length / (1024 * 1024);
    
    if (sizeInMb > 20) {
      alert(`The photos you selected are too large (Total: ${sizeInMb.toFixed(1)}MB). Please remove some photos or use smaller files. Cloudflare limits the database to 25MB.`);
      return;
    }

    setIsSavingProfile(true);
    
    fetch(`${API_URL}/api/apps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(profile)
    })
      .then(res => res.json())
      .then(() => {
        alert('Profile updated successfully!');
        setShowProfileEditor(false);
        fetchDashboardData();
      })
      .catch(err => alert('Error updating profile: ' + err.message))
      .finally(() => setIsSavingProfile(false));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach(file => {
      // Check file size (limit to 2MB per photo for speed)
      if (file.size > 2 * 1024 * 1024) {
        alert(`Photo "${file.name}" is too large. Please use photos under 2MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setProfile(prev => ({
          ...prev,
          screenshots: [...prev.screenshots, base64String]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setProfile(prev => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index)
    }));
  };

  const handleOpenEventForm = (event?: Event) => {
    if (event) {
      setEditingEvent(event);
      setEventFormData({ title: event.title, date: event.date, time: event.time, location: event.location, description: event.description });
    } else {
      setEditingEvent(null);
      setEventFormData({ title: '', date: '', time: '', location: '', description: '' });
    }
    setShowEventForm(true);
  };

  const handleEventSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const url = editingEvent ? `${API_URL}/events/${editingEvent.id}` : `${API_URL}/events`;
    const method = editingEvent ? 'PUT' : 'POST';

    fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(eventFormData)
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to save event');
        return data;
      })
      .then(() => {
        setShowEventForm(false);
        setEditingEvent(null);
        setEventFormData({ title: '', date: '', time: '', location: '', description: '' });
        fetchDashboardData();
      })
      .catch(err => {
        console.error('Error saving event:', err);
        alert(err.message);
      });
  };

  const handleDeleteEvent = (id: string) => {
    if (!window.confirm('Delete this event?')) return;
    fetch(`${API_URL}/events/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(() => fetchDashboardData());
  };

  const handleSaveDateDetails = () => {
    if (!editingDate) return;
    const { monthKey, date } = editingDate;
    
    setProfile({
      ...profile,
      monthlyAvailability: {
        ...profile.monthlyAvailability,
        [monthKey]: {
          ...(profile.monthlyAvailability[monthKey] || {}),
          [date]: dateEditorData
        }
      }
    });
    setEditingDate(null);
  };

  const handleBookingStatus = (id: string, status: string, reason?: string) => {
    fetch(`${API_URL}/api/bookings/${id}/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ status, declineReason: reason })
    })
      .then(res => res.json())
      .then(() => {
        setDecliningBookingId(null);
        setDeclineReason('');
        fetchDashboardData();
      })
      .catch(err => console.error('Error updating booking:', err));
  };

  const testPushNotification = () => {
    fetch(`${API_URL}/api/push/test`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(data => {
      if (data.success) alert('🚀 Test signal sent! Your phone should buzz in a few seconds.');
      else alert('Failed to send test: ' + (data.error || 'Unknown error'));
    })
    .catch(err => alert('Network error: ' + err.message));
  };

  if (authLoading || (loading && user)) return <div className="dashboard-container">Loading dashboard...</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div>
          <h1>Welcome, {user.name.split(' ')[0]}!</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            Logged in as <strong>{user.id.startsWith('sitter-') ? 'Sitter' : 'Parent'}</strong>
            {user.avatar && <img src={user.avatar} alt="Avatar" style={{ width: '30px', height: '30px', borderRadius: '50%', border: '1px solid #ddd' }} />}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button className="secondary-btn" style={{ backgroundColor: '#f0f4f8' }} onClick={() => navigate('/avatar-creator')}>
            {user.avatar ? 'Change My Avatar 🎨' : 'Design My Avatar 🎨'}
          </button>
          {user.id.startsWith('sitter-') && (
            <>
              <button className="secondary-btn" style={{ borderColor: 'var(--accent-color)', color: 'var(--accent-color)' }} onClick={testPushNotification}>Test Notifications 🔔</button>
              <button className="secondary-btn" onClick={() => setShowProfileEditor(true)}>Edit My Sitter Profile</button>
              <button className="secondary-btn" onClick={() => handleOpenEventForm()}>Add Event</button>
            </>
          )}
        </div>
      </header>

      {showProfileEditor && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h2>Edit Your Sitter Profile</h2>
            <form onSubmit={handleProfileSubmit} className="profile-form">
              <div className="form-group"><label>Display Name</label><input type="text" value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} /></div>
              <div className="form-group"><label>Category</label><input type="text" value={profile.category} onChange={e => setProfile({...profile, category: e.target.value})} /></div>
              <div className="form-group"><label>Price ($/hr)</label><input type="number" value={profile.price} onChange={e => setProfile({...profile, price: parseInt(e.target.value)})} /></div>
              <div className="form-group"><label>Short Description</label><input type="text" value={profile.description} onChange={e => setProfile({...profile, description: e.target.value})} /></div>
              <div className="form-group"><label>Neighborhood (Where you will babysit)</label><input type="text" placeholder="e.g. South End, Clayton Park..." value={profile.neighborhood} onChange={e => setProfile({...profile, neighborhood: e.target.value})} /></div>
              <div className="form-group"><label>Detailed Bio</label><textarea value={profile.detailedDescription} onChange={e => setProfile({...profile, detailedDescription: e.target.value})} /></div>
              
              <div className="form-group">
                <label>Weekly Availability (Select days when you are BUSY)</label>
                <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '15px', border: '1px solid #ddd', marginTop: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <button type="button" className="secondary-btn" onClick={() => {
                      const newDate = new Date(currentWeekStart);
                      newDate.setDate(newDate.getDate() - 7);
                      setCurrentWeekStart(newDate);
                    }}>← Prev Week</button>
                    <strong style={{ fontSize: '1rem' }}>
                      Week of {currentWeekStart.toLocaleDateString('default', { month: 'short', day: 'numeric' })} - {new Date(new Date(currentWeekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </strong>
                    <button type="button" className="secondary-btn" onClick={() => {
                      const newDate = new Date(currentWeekStart);
                      newDate.setDate(newDate.getDate() + 7);
                      setCurrentWeekStart(newDate);
                    }}>Next Week →</button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', textAlign: 'center' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                      <div key={d} style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#666', paddingBottom: '5px' }}>{d}</div>
                    ))}
                    {Array.from({ length: 7 }).map((_, i) => {
                      const d = new Date(currentWeekStart);
                      d.setDate(d.getDate() + i);
                      const dateStr = d.toISOString().split('T')[0];
                      const monthKey = dateStr.substring(0, 7);
                      const dayData = (profile.monthlyAvailability[monthKey] || {})[dateStr] || { status: 'Free', note: '' };
                      const isBusy = dayData.status === 'Busy';
                      const isToday = new Date().toISOString().split('T')[0] === dateStr;

                      return (
                        <button
                          key={dateStr}
                          type="button"
                          onClick={() => {
                            setEditingDate({ date: dateStr, monthKey });
                            setDateEditorData(dayData);
                          }}
                          style={{
                            padding: '12px 5px',
                            borderRadius: '10px',
                            border: isToday ? '2px solid var(--primary-color)' : '1px solid #ddd',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            backgroundColor: isBusy ? '#e74c3c' : 'white',
                            color: isBusy ? 'white' : '#333',
                            fontWeight: isBusy ? 'bold' : 'normal',
                            position: 'relative'
                          }}
                        >
                          <div style={{ fontSize: '0.7rem', opacity: 0.6 }}>{d.getDate()}</div>
                          <div style={{ fontSize: '0.6rem', marginTop: '2px' }}>{isBusy ? 'BUSY' : 'FREE'}</div>
                          {dayData.note && <div style={{ position: 'absolute', top: '2px', right: '2px', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isBusy ? 'white' : '#3498db' }}></div>}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: '15px', display: 'flex', gap: '15px', justifyContent: 'center', fontSize: '0.8rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><div style={{ width: '12px', height: '12px', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '3px' }}></div> Free</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><div style={{ width: '12px', height: '12px', backgroundColor: '#e74c3c', borderRadius: '3px' }}></div> Busy</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><div style={{ width: '6px', height: '6px', backgroundColor: '#3498db', borderRadius: '50%' }}></div> Has Note</div>
                  </div>
                </div>
              </div>

              {editingDate && (
                <div className="modal-overlay" style={{ zIndex: 1100 }}>
                  <div className="modal-card" style={{ maxWidth: '400px' }}>
                    <h3>Edit Availability: {editingDate.date}</h3>
                    <div className="form-group">
                      <label>Status</label>
                      <select 
                        value={dateEditorData.status} 
                        onChange={e => setDateEditorData({...dateEditorData, status: e.target.value})}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                      >
                        <option value="Free">Free</option>
                        <option value="Busy">Busy</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Details (What are you doing? What times?)</label>
                      <textarea 
                        placeholder="e.g. School 9-3, Soccer practice @ 4pm..." 
                        value={dateEditorData.note}
                        onChange={e => setDateEditorData({...dateEditorData, note: e.target.value})}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', minHeight: '80px' }}
                      />
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="cancel-btn" onClick={() => setEditingDate(null)}>Cancel</button>
                      <button type="button" className="save-btn" onClick={handleSaveDateDetails}>Save Day Details</button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="form-group">
                <label>Activity Photos</label>
                <input type="file" multiple accept="image/*" onChange={handleFileUpload} style={{ marginBottom: '10px' }} />
                <div className="photo-preview-grid" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {profile.screenshots.map((src, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={src} alt="Preview" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                      <button type="button" onClick={() => removePhoto(i)} style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#ff4d4d', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer' }}>×</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowProfileEditor(false)} disabled={isSavingProfile}>Cancel</button>
                <button 
                  type="submit" 
                  className="save-btn" 
                  disabled={isSavingProfile}
                  style={{ opacity: isSavingProfile ? 0.7 : 1, cursor: isSavingProfile ? 'not-allowed' : 'pointer' }}
                >
                  {isSavingProfile ? 'Saving Profile...' : 'Save Profile Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEventForm && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h2>{editingEvent ? 'Edit Event' : 'Add Upcoming Event'}</h2>
            <form onSubmit={handleEventSubmit} className="profile-form">
              <div className="form-group"><label>Event Title</label><input type="text" required value={eventFormData.title} onChange={e => setEventFormData({...eventFormData, title: e.target.value})} /></div>
              <div className="form-group"><label>Date</label><input type="text" placeholder="e.g. April 15, 2026" required value={eventFormData.date} onChange={e => setEventFormData({...eventFormData, date: e.target.value})} /></div>
              <div className="form-group"><label>Time</label><input type="text" placeholder="e.g. 10:00 AM - 2:00 PM" required value={eventFormData.time} onChange={e => setEventFormData({...eventFormData, time: e.target.value})} /></div>
              <div className="form-group"><label>Location</label><input type="text" required value={eventFormData.location} onChange={e => setEventFormData({...eventFormData, location: e.target.value})} /></div>
              <div className="form-group"><label>Description</label><textarea required value={eventFormData.description} onChange={e => setEventFormData({...eventFormData, description: e.target.value})} /></div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowEventForm(false)}>Cancel</button>
                <button type="submit" className="save-btn">{editingEvent ? 'Update Event' : 'Post Event'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SHOPPING ORDERS SECTION (SITTERS ONLY) - HIDDEN FOR NOW */}
      {/*
      {user.id.startsWith('sitter-') && (
        <div className="bookings-list" style={{ marginBottom: '40px' }}>
          <h2>🛒 Incoming Shopping Orders</h2>
          {orders.length === 0 ? <p>No shopping orders yet.</p> : orders.map(order => (
            <div key={order.id} className="booking-item-card" style={{ borderLeft: '6px solid #2ecc71' }}>
              <div className="booking-main">
                <div className="booking-info-group">
                  <label>Item</label>
                  <strong style={{ fontSize: '1.1rem' }}>
                    {order.quantity}x {order.itemName} 
                    {order.selectedEdition && order.selectedEdition !== 'Standard' && (
                      <span style={{ color: '#3498db', marginLeft: '8px' }}>({order.selectedEdition})</span>
                    )}
                  </strong>
                </div>
                <div className="booking-info-group">
                  <label>Customer & Address</label>
                  <span>{order.parentName} - {order.address}</span>
                </div>
                <div className="booking-info-group">
                  <label>Delivery Date</label>
                  <span style={{ color: '#27ae60', fontWeight: 'bold' }}>{order.deliveryDate} ({order.deliveryType})</span>
                </div>
                <div className="booking-info-group">
                  <label>Collect Payment</label>
                  <span style={{ color: '#e67e22', fontWeight: 'bold' }}>${order.totalPrice} CASH</span>
                </div>
                
                <div className="booking-info-group" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #eee' }}>
                  <label style={{ color: '#666', fontSize: '0.75rem' }}>PROOF OF ORDER (Digital Signature)</label>
                  <div style={{ fontFamily: '"Caveat", cursive', fontSize: '1.4rem', color: '#2c3e50', marginTop: '5px' }}>
                    {order.signature || 'No signature provided'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      */}

      {/* CUSTOMER INQUIRIES SECTION */}
      {user.id.startsWith('sitter-') && (
        <div className="bookings-list" style={{ marginBottom: '40px' }}>
          <h2>💬 Customer Inquiries</h2>
          {contactMessages.length === 0 ? <p>No active inquiries.</p> : contactMessages.map(msg => (
            <div key={msg.id} className="booking-item-card" style={{ borderLeft: '6px solid #3498db' }}>
              <div className="booking-main">
                <div className="booking-info-group">
                  <label>Customer</label>
                  <span>{msg.name} ({msg.email})</span>
                </div>
                <div className="booking-info-group">
                  <label>Latest Message</label>
                  <span style={{ fontStyle: 'italic' }}>"{msg.message}"</span>
                </div>
              </div>
              <div className="booking-actions">
                <button 
                  className="view-btn" 
                  onClick={() => navigate('/messaging', { state: { chatId: msg.id, name: msg.name, email: msg.email } })}
                >
                  Respond to Chat
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MY BOOKINGS */}
      <div className="bookings-list">
        <h2>My Bookings</h2>
        {bookings.length === 0 ? <p>No bookings yet.</p> : bookings.map(b => (
          <div key={b.id} className="booking-item-card">
            <div className={`booking-status ${b.status.toLowerCase()}`}>{b.status}</div>
            <div className="booking-main" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
                <div className="booking-info-group">
                  <label>Customer</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {b.parentAvatar && <img src={b.parentAvatar} alt="" style={{ width: '30px', height: '30px', borderRadius: '50%' }} />}
                    <span>{b.parentName}</span>
                  </div>
                </div>
                <div className="booking-info-group"><label>Time</label><span>{b.date} @ {b.startTime}</span></div>
                {b.emergencyPhone && (
                  <div className="booking-info-group">
                    <label>🚨 Emergency Contact</label>
                    <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>{b.emergencyPhone}</span>
                  </div>
                )}
                <div className="booking-info-group">
                  <label>Address</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span>{b.address}</span>
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.address + ", Nova Scotia")}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="view-btn"
                      style={{ padding: '4px 8px', fontSize: '0.75rem', textDecoration: 'none', backgroundColor: '#4285F4', color: 'white' }}
                    >
                      📍 View on Maps
                    </a>
                  </div>
                </div>
              </div>
              
              <div style={{ padding: '10px 0', borderTop: '1px solid #eee' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>Children Details ({b.numChildren || 1} total)</label>
                  {b.totalPrice && <span style={{ fontWeight: 'bold', color: '#27ae60' }}>Total: ${b.totalPrice} (${b.hourlyRate}/hr)</span>}
                </div>
                
                {b.children ? b.children.map((child, idx) => (
                  <div key={idx} style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '10px', border: '1px solid #eee' }}>
                    <div style={{ display: 'flex', gap: '15px', marginBottom: '5px', flexWrap: 'wrap' }}>
                      <div className="booking-info-group"><label>Name</label><span>{child.name}</span></div>
                      <div className="booking-info-group"><label>Age</label><span>{child.age} yrs</span></div>
                      <div className="booking-info-group"><label>Gender</label><span>{child.gender}</span></div>
                    </div>
                    {child.allergies && <div className="booking-info-group"><label>Allergies</label><span style={{ color: '#e74c3c' }}>{child.allergies}</span></div>}
                    {child.specialInstructions && <div className="booking-info-group"><label>Instructions</label><span style={{ fontStyle: 'italic' }}>"{child.specialInstructions}"</span></div>}
                  </div>
                )) : (
                  <div style={{ padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '10px' }}>
                    <div className="booking-info-group"><label>Name</label><span>Legacy Booking (No child details)</span></div>
                  </div>
                )}
              </div>

              {b.status.toLowerCase() === 'denied' && b.declineReason && (
                <div className="booking-info-group" style={{ padding: '10px', backgroundColor: '#fff5f5', borderRadius: '8px', borderLeft: '4px solid #e74c3c', marginTop: '10px' }}>
                  <label style={{ color: '#e74c3c' }}>Reason for Decline</label>
                  <span style={{ fontSize: '0.95rem', color: '#333' }}>{b.declineReason}</span>
                </div>
              )}

              {/* ACTIONS: ONLY SITTERS CAN SEE ACTIONS */}
              {user.id.startsWith('sitter-') && (
                <div className="booking-actions" style={{ marginTop: '10px' }}>
                  {b.status.toLowerCase() === 'pending' && (
                    <button className="view-btn" style={{ backgroundColor: '#27ae60', color: 'white' }} onClick={() => handleBookingStatus(b.id, 'Accepted')}>Accept</button>
                  )}
                  {(b.status.toLowerCase() === 'pending' || b.status.toLowerCase() === 'accepted') && decliningBookingId !== b.id && (
                    <button className="decline-btn" onClick={() => setDecliningBookingId(b.id)}>
                      {b.status.toLowerCase() === 'accepted' ? 'Cancel Booking' : 'Decline'}
                    </button>
                  )}

                  {decliningBookingId === b.id && (
                    <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '12px', border: '1px solid #ddd', width: '100%' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Reason for {b.status.toLowerCase() === 'accepted' ? 'Cancellation' : 'Decline'}:</label>
                      <textarea 
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        placeholder="e.g. Already booked, family emergency, etc."
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', minHeight: '80px', marginBottom: '10px', fontFamily: 'inherit' }}
                      ></textarea>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                          className="decline-btn" 
                          style={{ flex: 1 }}
                          onClick={() => handleBookingStatus(b.id, 'Denied', declineReason)}
                        >
                          Confirm {b.status.toLowerCase() === 'accepted' ? 'Cancel' : 'Decline'}
                        </button>
                        <button 
                          className="secondary-btn" 
                          style={{ flex: 1, padding: '10px' }}
                          onClick={() => { setDecliningBookingId(null); setDeclineReason(''); }}
                        >
                          Keep Booking
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {user.id.startsWith('sitter-') && (
        <div className="bookings-list" style={{ marginTop: '40px' }}>
          <h2>Event Manager</h2>
          {events.length === 0 ? <p>No events posted.</p> : events.map(e => (
            <div key={e.id} className="booking-item-card">
              <div className="booking-main">
                <div className="booking-info-group"><label>Event</label><span>{e.title}</span></div>
                <div className="booking-info-group"><label>Date</label><span>{e.date}</span></div>
              </div>
              <div className="booking-actions" style={{ display: 'flex', gap: '10px' }}>
                <button className="view-btn" onClick={() => handleOpenEventForm(e)}>Edit Event</button>
                <button className="decline-btn" onClick={() => handleDeleteEvent(e.id)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
